'use client'

import { useState } from 'react'
import { createClient } from '../utils/supabase/client'
import { Button } from '@wayak/ui/components/ui/button'
import { OAUTH_PROVIDERS } from '../utils/config'

function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  )
}

function MicrosoftIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 23 23">
      <rect x="1" y="1" width="10" height="10" fill="#f25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
      <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
      <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
    </svg>
  )
}

const providerConfig = {
  google: {
    label: 'Google',
    icon: GoogleIcon,
    scopes: undefined,
  },
  azure: {
    label: 'Microsoft',
    icon: MicrosoftIcon,
    scopes: 'email',
  },
} as const

type OAuthProvider = keyof typeof providerConfig

export function OAuthButtons() {
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const enabledProviders = OAUTH_PROVIDERS.filter(
    (p): p is OAuthProvider => p in providerConfig
  )

  if (enabledProviders.length === 0) return null

  const handleOAuthSignIn = async (provider: OAuthProvider) => {
    const supabase = createClient()
    setLoadingProvider(provider)
    setError(null)

    try {
      const config = providerConfig[provider]
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/confirm`,
          ...(config.scopes ? { scopes: config.scopes } : {}),
        },
      })
      if (error) throw error
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'OAuth sign-in failed')
      setLoadingProvider(null)
    }
  }

  return (
    <div className="flex flex-col gap-4 mt-4">
      <div className="relative flex items-center">
        <div className="flex-grow border-t border-border" />
        <span className="mx-4 flex-shrink text-xs text-muted-foreground">
          or continue with
        </span>
        <div className="flex-grow border-t border-border" />
      </div>

      <div className="flex flex-col gap-2">
        {enabledProviders.map((provider) => {
          const config = providerConfig[provider]
          const Icon = config.icon
          const isLoading = loadingProvider === provider
          return (
            <Button
              key={provider}
              type="button"
              variant="outline"
              className="w-full"
              disabled={loadingProvider !== null}
              onClick={() => handleOAuthSignIn(provider)}
            >
              {isLoading ? (
                'Redirecting...'
              ) : (
                <>
                  <Icon />
                  Continue with {config.label}
                </>
              )}
            </Button>
          )
        })}
      </div>

      {error && <p className="text-sm text-red-500 text-center">{error}</p>}
    </div>
  )
}
